import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EXAMPLES = [
  "Raise data-storytelling competency among district collectors handling Aspirational Districts Programme dashboards",
  "Prepare state cyber-incident response teams for monsoon-scale phishing attacks on welfare portals",
  "Upskill BDOs to integrate climate-resilient planning into MGNREGA shelf of works",
];

export default function Translator() {
  const [demand, setDemand] = useState(EXAMPLES[0]);
  const [days, setDays] = useState(3);
  const [audience, setAudience] = useState("District Magistrates & ADMs");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!demand.trim()) return toast.error("Please enter a demand signal");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/translator/generate",
        { demand_signal: demand, duration_days: days, target_audience: audience },
        { timeout: 180000 }
      );
      setResult(data);
      toast.success("Programme generated");
    } catch (e) {
      const msg = e?.code === "ECONNABORTED"
        ? "LLM took too long. Please try again."
        : "Could not generate programme. Please retry.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="translator-module" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <div className="border border-white/10 bg-[#13233F] p-6">
          <div className="font-mono-data text-xs tracking-[0.25em] uppercase text-[#E8B298] mb-4">CBP · Demand Signal</div>
          <label className="block text-xs text-cream/60 mb-2 font-mono-data uppercase tracking-[0.18em]">Raw demand</label>
          <textarea data-testid="translator-input" value={demand} onChange={e=>setDemand(e.target.value)}
                    rows={5}
                    className="w-full bg-navy border border-white/15 text-cream p-3 text-sm font-mono-data focus:outline-none focus:border-terracotta" />
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-xs text-cream/60 mb-2 font-mono-data uppercase tracking-[0.18em]">Duration · days</label>
              <input type="number" min={1} max={30} value={days} onChange={e=>setDays(Number(e.target.value))}
                     data-testid="translator-days"
                     className="w-full bg-navy border border-white/15 text-cream p-3 text-sm font-mono-data" />
            </div>
            <div>
              <label className="block text-xs text-cream/60 mb-2 font-mono-data uppercase tracking-[0.18em]">Audience</label>
              <input value={audience} onChange={e=>setAudience(e.target.value)} data-testid="translator-audience"
                     className="w-full bg-navy border border-white/15 text-cream p-3 text-sm" />
            </div>
          </div>

          <button data-testid="translator-generate-btn" onClick={generate} disabled={loading}
                  className="mt-6 w-full bg-terracotta text-white font-medium py-3 hover:bg-[#c44e2e] disabled:opacity-60">
            {loading ? "Generating…" : "Translate to Programme →"}
          </button>

          <div className="mt-6 border-t border-white/10 pt-4">
            <div className="font-mono-data text-[10px] tracking-[0.25em] uppercase text-cream/50 mb-2">Examples</div>
            <div className="space-y-1.5">
              {EXAMPLES.map((e,i) => (
                <button key={i} data-testid={`translator-example-${i}`}
                        onClick={()=>setDemand(e)}
                        className="text-left w-full text-xs text-cream/70 hover:text-[#E8B298] leading-relaxed">
                  · {e}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="border border-white/10 bg-[#13233F] min-h-[540px] p-6 relative">
          <div className="font-mono-data text-xs tracking-[0.25em] uppercase text-[#E8B298] mb-4">AI · Competency-Linked Supply</div>
          {!result && !loading && (
            <div className="flex flex-col items-center justify-center h-[440px] text-cream/40 text-sm font-mono-data">
              <div className="w-24 h-24 chakra-radial rounded-full opacity-60 mb-4" />
              Structured programme will appear here.
            </div>
          )}
          {loading && (
            <div className="flex flex-col items-center justify-center h-[440px] gap-4">
              <div className="w-14 h-14 chakra-radial rounded-full orbit-slow opacity-80" />
              <div className="font-mono-data text-xs tracking-[0.25em] uppercase text-[#E8B298]">Thinking through competencies…</div>
            </div>
          )}
          {result && (
            <div data-testid="translator-result" className="space-y-6">
              <div>
                <h3 className="font-serif-display text-2xl leading-tight">{result.programme_title}</h3>
                <p className="text-sm text-cream/70 mt-2">{result.rationale}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {result.target_competencies?.map(c => (
                  <span key={c} className="text-[11px] font-mono-data uppercase tracking-[0.18em] px-2.5 py-1 bg-terracotta/15 text-[#E8B298] border border-terracotta/30">
                    {c}
                  </span>
                ))}
              </div>
              <div className="border-t border-white/10 pt-5">
                <div className="font-mono-data text-[10px] tracking-[0.25em] uppercase text-cream/50 mb-3">Modules</div>
                <div className="space-y-4">
                  {result.modules?.map((m,i) => (
                    <div key={`${m.title}-${i}`} data-testid={`translator-module-${i}`} className="grid grid-cols-12 gap-4 border-l-2 border-[#4C956C] pl-4">
                      <div className="col-span-1 font-mono-data text-xs text-cream/50">{String(i+1).padStart(2,'0')}</div>
                      <div className="col-span-11">
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="font-serif-display text-lg">{m.title}</div>
                          <div className="font-mono-data text-[11px] text-cream/60">{m.hours}h · {m.pedagogy}</div>
                        </div>
                        <div className="text-xs text-[#E8B298] font-mono-data tracking-[0.18em] uppercase mt-0.5">{m.competency}</div>
                        <ul className="mt-2 space-y-1">
                          {m.learning_outcomes?.map((o,k)=>(
                            <li key={`${o}-${k}`} className="text-sm text-cream/80 flex gap-2"><span className="text-[#4C956C]">•</span>{o}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/10">
                <div>
                  <div className="font-mono-data text-[10px] tracking-[0.25em] uppercase text-cream/50 mb-1">Suggested Faculty</div>
                  <div className="text-sm text-cream/85">{result.suggested_faculty_profile}</div>
                </div>
                <div>
                  <div className="font-mono-data text-[10px] tracking-[0.25em] uppercase text-cream/50 mb-1">Assessment Strategy</div>
                  <div className="text-sm text-cream/85">{result.assessment_strategy}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
